import React, { useEffect, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  withSpring,
  Easing,
} from "react-native-reanimated";

const PARTICLE_COUNT = 14;
const COLORS = [
  "#A78BFA", // purple
  "#4ADE80", // green
  "#FBBF24", // gold
  "#F472B6", // pink
  "#60A5FA", // blue
  "#34D399", // emerald
  "#FB923C", // orange
];

interface Particle {
  color: string;
  /** Angle in radians for burst direction */
  angle: number;
  /** Distance to travel */
  distance: number;
  /** Start delay in ms */
  delay: number;
  /** Size of the particle */
  size: number;
  /** Whether this is a square or circle */
  isSquare: boolean;
  /** Rotation for squares */
  rotation: number;
}

function generateParticles(): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const angle =
      (i / PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
    return {
      color: COLORS[i % COLORS.length]!,
      angle,
      distance: 40 + Math.random() * 50,
      delay: Math.random() * 80,
      size: 4 + Math.random() * 5,
      isSquare: Math.random() > 0.5,
      rotation: Math.random() * 360,
    };
  });
}

function ConfettiParticle({
  particle,
  trigger,
}: {
  particle: Particle;
  trigger: number;
}) {
  const progress = useSharedValue(0);
  const fadeOut = useSharedValue(1);

  useEffect(() => {
    if (trigger > 0) {
      progress.value = 0;
      fadeOut.value = 1;
      progress.value = withDelay(
        particle.delay,
        withSpring(1, { damping: 12, stiffness: 140, mass: 0.6 }),
      );
      fadeOut.value = withDelay(
        particle.delay + 400,
        withTiming(0, { duration: 350, easing: Easing.out(Easing.ease) }),
      );
    }
  }, [trigger, particle.delay, progress, fadeOut]);

  const animatedStyle = useAnimatedStyle(() => {
    const x = Math.cos(particle.angle) * particle.distance * progress.value;
    const y =
      Math.sin(particle.angle) * particle.distance * progress.value -
      15 * progress.value;

    return {
      transform: [
        { translateX: x },
        { translateY: y },
        { scale: 1 - progress.value * 0.4 },
        { rotate: `${particle.rotation * progress.value}deg` },
      ],
      opacity: fadeOut.value,
    };
  });

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          width: particle.size,
          height: particle.size,
          backgroundColor: particle.color,
          borderRadius: particle.isSquare ? 1.5 : particle.size / 2,
        },
        animatedStyle,
      ]}
    />
  );
}

interface ConfettiBurstProps {
  /** Increment this number to trigger a new burst */
  trigger: number;
  /** Called when the burst animation completes */
  onComplete?: () => void;
}

/**
 * Animated confetti burst overlay. Position this absolutely
 * over the element you want confetti to burst from.
 *
 * Usage:
 * ```tsx
 * <View style={{ position: "relative" }}>
 *   <ConfettiBurst trigger={burstCount} />
 *   <YourButton onPress={() => setBurstCount(c => c + 1)} />
 * </View>
 * ```
 */
export function ConfettiBurst({ trigger, onComplete }: ConfettiBurstProps) {
  const particles = useMemo(() => generateParticles(), []);

  useEffect(() => {
    if (trigger > 0 && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 900);
      return () => clearTimeout(timer);
    }
  }, [trigger, onComplete]);

  if (trigger === 0) return null;

  return (
    <View style={styles.container} pointerEvents="none">
      {particles.map((particle, i) => (
        <ConfettiParticle key={i} particle={particle} trigger={trigger} />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    zIndex: 10,
  },
});
